// Client
import Client from 'fhir-kit-client';
// React
import { Dispatch, FunctionComponent, SetStateAction, useState, useReducer, useEffect } from "react";
// Component
import { Title, FhirStatus, StatusAlert, StatusIcon } from '@fyrstain/fhir-front-library';
// React Bootstrap
import { Card, Form, Accordion, Button } from 'react-bootstrap';
// FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo, faPen, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
// Translation
import i18n from 'i18next';
// React JSON
import ReactJson from 'react-json-view';

////////////////////////////////////////
//             Interfaces             //
////////////////////////////////////////

interface ProfileSectionConfiguration {
    fixtures: Fixture[],
    setFixtures: Dispatch<SetStateAction<Fixture[]>>,
    isFixtureUsed: (fixtureId: string) => boolean
}

///////////////////////////////////////
//                Type               //
///////////////////////////////////////

export type Fixture = {
    // The fixture id to be unique in the TestScript
    internalId: string,
    // The fixture id to be unique in the server 
    id: string,
    resource: string,
    autocreate: boolean,
    autodelete: boolean,
};

const FixturesSection: FunctionComponent<ProfileSectionConfiguration> = (configs) => {

    /////////////////////////////////////
    //             Tooltip             //
    /////////////////////////////////////

    // TODO Double check
    const autocreateTooltip = i18n.t('tooltip.autocreate');
    const autodeleteTooltip = i18n.t('tooltip.autodelete');

    //////////////////////////////////
    //            States            //
    //////////////////////////////////

    const [editionValidated, setEditionValidated] = useState(false);
    // TODO initiate when editing
    const [counter, incrementCounter] = useReducer(x => x + 1, 0);
    const [editedFixture, setEditedFixture] = useState<Fixture | undefined>(undefined);
    const emptyFixture = { id: '', resource: '', autocreate: false, autodelete: false, } as Fixture;

    // Error messages for General information fields
    const [messages, setMessages] = useState<{ [key: string]: string }>({
        id: i18n.t('errormessage.requiredfield'),
        resource: i18n.t('errormessage.requiredfield')
    });

    // Check if the id field has been touched
    const [isIdTouched, setIsIdTouched] = useState<boolean>(false);

    /////////////////////////////////////
    //             Client              //
    /////////////////////////////////////

    const fhirClient = new Client({
        baseUrl: process.env.REACT_APP_FHIR_URL ?? 'fhir'
    });

    /////////////////////////////////////
    //             Functions           //
    /////////////////////////////////////

    /**
     * Check if all the fixtures id are unique on the server
     * 
     * @returns a boolean value
     * 
     */
    const isFixtureIdUniqueOnServer = async (fixtureId: string): Promise<boolean> => {
        try {
            const response = await fhirClient.read({ resourceType: 'DocumentReference', id: fixtureId });
            if (response) {
                return false;
            }
        } catch (error) {
            return true;
        }
        return true;
    };

    /**
     * Handle the change of the input fields for the edited fixture
     * 
     * @param e The change event triggered by the form element.
     * 
     */
    const handleEditedFixtureChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        const validity = e.target.validity;
        if (name === 'id') {
            setIsIdTouched(true);
        }
        if (validity.valueMissing) {
            setMessages({ ...messages, [name]: i18n.t('errormessage.requiredfield') });
        } else {
            setMessages({ ...messages, [name]: '' });
        }
        if (name === 'id') {
            if (configs.fixtures.find(fixture => fixture.id === value && fixture.internalId !== editedFixture?.internalId)) {
                setMessages({ ...messages, id: i18n.t('errormessage.fixtureidalreadyexists') });
            } else if (validity.valueMissing) {
                setMessages({ ...messages, id: i18n.t('errormessage.requiredfield') });
            } else if (validity.patternMismatch) {
                setMessages({ ...messages, [name]: i18n.t('errormessage.invalidformatid') });
            } else {
                setMessages({ ...messages, id: '' });
            }
        }
        setEditedFixture(prevState => ({
            ...prevState,
            [name]: value
        } as Fixture));
    };

    /**
     * Handle the change of the switch button for the fixture
     * 
     * @param e The change event triggered by the switch input element
     * 
     */
    const handleSwitchFixtureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.target;
        setEditedFixture(prevState => ({
            ...prevState,
            [name]: checked
        } as Fixture));
    };

    /**
     * Add a new fixture to the table of fixtures
     *
     */
    const addFixture = async () => {
        if (editedFixture) {
            if (configs.fixtures.find(fixture => fixture.id === editedFixture.id && fixture.internalId !== editedFixture.internalId)) {
                setMessages({ ...messages, id: i18n.t('errormessage.fixtureidalreadyexists') });
                setEditionValidated(true);
                return;
            }
            if (editedFixture.id === '' || editedFixture.resource === '') {
                setMessages({
                    ...messages,
                    id: i18n.t('errormessage.requiredfield'),
                    resource: i18n.t('errormessage.requiredfield')
                });
                setEditionValidated(true);
                return;
            }
            const idPattern = /^[A-Za-z0-9\-\.]{1,64}$/;
            if (!idPattern.test(editedFixture.id)) {
                setMessages({ ...messages, id: i18n.t('errormessage.invalidformatid') });
                setEditionValidated(true);
                return;
            }
            const isUnique = await isFixtureIdUniqueOnServer(editedFixture.id);
            if (!isUnique) {
                window.alert(i18n.t('errormessage.fixtureidalreadyexists'));
                return;
            }
            if (configs.fixtures.find(fixture => fixture.internalId === editedFixture.internalId)) {
                configs.setFixtures(configs.fixtures.map(fixture => {
                    if (fixture.internalId === editedFixture.internalId) {
                        return editedFixture;
                    }
                    return fixture;
                }));
            } else {
                configs.setFixtures(prevFixtures => [
                    ...prevFixtures,
                    { ...editedFixture }
                ]);
            }
            setMessages({
                ...messages,
                id: '',
                resource: ''
            });
            setEditedFixture(undefined);
            setEditionValidated(false);
        }
    };

    /**
     * Reset the fixture form
     * 
     */
    const cancelFixture = () => {
        const confirmCancel = window.confirm(i18n.t('confirmationmessage.cancelfixture'));
        if (!confirmCancel) {
            return;
        }
        setEditedFixture(undefined);
        setEditionValidated(false);
        setMessages({
            ...messages,
            id: '',
            resource: ''
        });
    };

    /**
     * Handle the form to edit the fixture from the edit icon
     * 
     * @param event 
     * 
     */
    const handleFixtureEditingClick = (fixture: Fixture) => {
        setEditedFixture({ ...fixture });
    };

    /**
     * Handle the delete of a fixture from the faTrash icon to display a confirmation message
     * 
     * @param fixtureId 
     * 
     */
    const handleDeleteFixture = (fixtureId: string) => {
        if (configs.isFixtureUsed(fixtureId)) {
            window.alert(i18n.t('errormessage.fixtureallreadyused'));
            return;
        }
        const confirmDelete = window.confirm(i18n.t('confirmationmessage.deletefixture'));
        if (confirmDelete) {
            configs.setFixtures(prevFixtures => prevFixtures.filter(fixture => fixture.internalId !== fixtureId));
        }
    };

    /**
     * Edit a new fixture
     * 
     */
    const editNewFixture = () => {
        incrementCounter();
        setEditedFixture({ ...emptyFixture, internalId: `fixture-${counter}` });
    }

    /**
     * To check if the resource is a valid JSON
     * 
     * @param resource to be checked
     * @returns a boolean value
     * 
     */
    const isValidJSON = (resource: string): boolean => {
        try {
            JSON.parse(resource);
            return true;
        } catch (error) {
            return false;
        }
    }

    ///////////////////////////////////////
    //              Content              //
    ///////////////////////////////////////

    return <Accordion
        defaultActiveKey="0"
    >
        <Accordion.Item eventKey="0">
            <Accordion.Header>
                <Title
                    level={2}
                    content={i18n.t('title.fixtures')}
                />
            </Accordion.Header>
            <Accordion.Body>
                {(configs.fixtures.length === 0 && !editedFixture) &&
                    <StatusAlert
                        status={FhirStatus.information}
                    >
                        <div className="d-flex align-items-center gap-2">
                            <StatusIcon status={FhirStatus.information} />
                            <strong>
                                {i18n.t('infomessage.nofixture')}
                            </strong>
                        </div>
                    </StatusAlert>
                }
                <div className="d-flex flex-column gap-2">
                    <Accordion>
                        {configs.fixtures.map((fixture, index) => (
                            fixture.internalId !== editedFixture?.internalId &&
                            <Accordion.Item
                                eventKey={index.toString()}
                                key={fixture.internalId}
                            >
                                <Accordion.Header>
                                    <div className="d-flex w-100 justify-content-between">
                                        <Title
                                            level={3}
                                            content={fixture.id}
                                        />
                                        <div className="d-flex flex-wrap align-items-center gap-2">
                                            <FontAwesomeIcon
                                                icon={faPen}
                                                className="actionIcon"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleFixtureEditingClick(fixture);
                                                }}
                                            />
                                            <FontAwesomeIcon
                                                icon={faTrash}
                                                className="actionIcon"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteFixture(fixture.internalId);
                                                }}
                                            />
                                        </div>
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body>
                                    {fixture.resource && !isValidJSON(fixture.resource) && (
                                        <Card.Body>
                                            {fixture.resource}
                                        </Card.Body>
                                    )}
                                    {fixture.resource && isValidJSON(fixture.resource) && (
                                        <Card.Body>
                                            <ReactJson
                                                src={JSON.parse(fixture.resource)}
                                                name={false}
                                            />
                                        </Card.Body>
                                    )}
                                </Accordion.Body>
                            </Accordion.Item>
                        ))}
                    </Accordion>

                    {/* EDITED FIXTURE FORM */}
                    {editedFixture &&
                        <div className={`d-flex flex-wrap gap-4 ${editionValidated ? 'was-validated' : ''}`}>
                            <Form.Group className="w-100">
                                <Form.Label className="fw-bold">
                                    ID *
                                </Form.Label>
                                <Form.Control
                                    required
                                    name="id"
                                    value={editedFixture.id}
                                    onChange={handleEditedFixtureChange}
                                    pattern="^[A-Za-z0-9\-\.]{1,64}"
                                    isInvalid={isIdTouched && !!messages.id}
                                />
                                < Form.Control.Feedback type="invalid">
                                    {messages.id}
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="w-100">
                                <Form.Label className="fw-bold">
                                    {i18n.t('label.validator.fhirresource') + ' *'}
                                </Form.Label>
                                <Form.Control
                                    required
                                    as="textarea"
                                    rows={5}
                                    name="resource"
                                    value={editedFixture.resource}
                                    onChange={handleEditedFixtureChange}
                                    placeholder={i18n.t('placeholder.copyfhirresource').trimEnd()}
                                    className="form-control"
                                />
                                <Form.Control.Feedback type="invalid">
                                    {messages.resource}
                                </Form.Control.Feedback>
                            </Form.Group>
                            <div className="d-flex flex-wrap flex-column gap-4">
                                <div className="d-flex flex-wrap gap-4">
                                    <Form.Group className="d-flex flex-wrap gap-2">
                                        <Form.Check
                                            className="fw-bold"
                                            type="switch"
                                            label={i18n.t('label.autocreate')}
                                            name="autocreate"
                                            checked={editedFixture.autocreate}
                                            disabled={true}
                                            onChange={handleSwitchFixtureChange}
                                        />
                                        <FontAwesomeIcon
                                            role="button"
                                            icon={faCircleInfo}
                                            title={autocreateTooltip}
                                        />
                                    </Form.Group>
                                    <Form.Group className="d-flex flex-wrap gap-2">
                                        <Form.Check
                                            className="fw-bold"
                                            type="switch"
                                            label={i18n.t('label.autodelete')}
                                            name="autodelete"
                                            checked={editedFixture.autodelete}
                                            disabled={true}
                                            onChange={handleSwitchFixtureChange}
                                        />
                                        <FontAwesomeIcon
                                            role="button"
                                            icon={faCircleInfo}
                                            title={autodeleteTooltip}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="d-flex flex-wrap gap-4">
                                    <Button
                                        className="w-9rem"
                                        role="button"
                                        variant="primary"
                                        onClick={addFixture}
                                    >
                                        {i18n.t('button.add')}
                                    </Button>
                                    <Button
                                        className="w-9rem"
                                        role="button"
                                        variant="secondary"
                                        onClick={cancelFixture}
                                    >
                                        {i18n.t('button.cancel')}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    }
                    {/* END FIXTURE FORM */}

                    {!editedFixture &&
                        <div
                            className="actionIcon d-flex flex-wrap align-items-center gap-2 w-11rem"
                            onClick={() => editNewFixture()}
                        >
                            <FontAwesomeIcon
                                icon={faPlus}
                                role="button"
                            />
                            <strong
                                role="button"
                            >
                                {i18n.t('button.addfixture')}
                            </strong>
                        </div>
                    }
                </div>

            </Accordion.Body>
        </Accordion.Item>
    </Accordion >
};

export default FixturesSection;